/**
 * useReviewAnomalyAlert — supervisor confirm / dismiss action on an
 * open anomaly_alerts row (PROJECT_STATE task #68).
 *
 * Two terminal modes:
 *   - mode='confirm': supervisor agrees, follow-up will be taken
 *                     offline. Status flips to 'confirmed', stamps
 *                     reviewed_at/by, optional reviewer_notes saved.
 *   - mode='dismiss': supervisor judged it a false positive. Status
 *                     flips to 'dismissed', same stamping.
 *
 * RLS gates this update to supervisor-and-up in the row's org. Cleaners
 * / maintenance / client_view get RLS-rejected; the hook surfaces
 * AppError with FORBIDDEN code.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { useAuthStore }                                         from '@/store/authStore'
import { AppError }                                             from '@/lib/errors/AppError'
import type { AnomalyAlertRow }                                 from './useAnomalyAlerts'

export type ReviewMode = 'confirm' | 'dismiss'

export interface ReviewVars {
  alert:           AnomalyAlertRow
  mode:            ReviewMode
  reviewerNotes?: string
}

export interface ReviewResult {
  id:                  string
  status:              'confirmed' | 'dismissed'
  reviewed_at:         string
  reviewed_by_user_id: string
}

export function useReviewAnomalyAlert(): UseMutationResult<ReviewResult, AppError, ReviewVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()
  const userId      = useAuthStore((s) => s.user?.id ?? null)

  return useMutation<ReviewResult, AppError, ReviewVars>({
    mutationFn: async ({ alert, mode, reviewerNotes }): Promise<ReviewResult> => {
      if (!userId) {
        throw new AppError({
          code:        'UNAUTHENTICATED',
          message:     'No user id in auth store',
          userMessage: 'Please sign in again before reviewing this alert.',
          status:      401,
        })
      }
      if (alert.status !== 'open') {
        throw new AppError({
          code:        'ALREADY_REVIEWED',
          message:     `cannot re-review when status is "${alert.status}"`,
          userMessage: 'This alert has already been reviewed.',
          status:      422,
        })
      }
      const trimmedNotes = (reviewerNotes ?? '').trim()
      const patch: Record<string, unknown> = {
        status:              mode === 'confirm' ? 'confirmed' : 'dismissed',
        reviewed_at:         new Date().toISOString(),
        reviewed_by_user_id: userId,
      }
      if (trimmedNotes.length > 0) patch.reviewer_notes = trimmedNotes

      const { data, error } = await supabase
        .from('anomaly_alerts')
        .update(patch)
        .eq('id', alert.id)
        .select('id, status, reviewed_at, reviewed_by_user_id')
        .single()
      if (error) {
        const code = mapPgErrorCode(error.code)
        throw new AppError({
          code,
          message:     error.message,
          userMessage: userMessageFor(code, error.message),
          status:      403,
        })
      }
      return data as unknown as ReviewResult
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['anomaly-alerts'] })
    },
  })
}

function mapPgErrorCode(pgCode: string | undefined): string {
  switch (pgCode) {
    case '42501':    return 'FORBIDDEN'
    case 'PGRST116': return 'NOT_FOUND'
    default:         return 'UPDATE_FAILED'
  }
}

function userMessageFor(code: string, fallback: string): string {
  switch (code) {
    case 'FORBIDDEN':  return 'You do not have permission to review this alert.'
    case 'NOT_FOUND':  return 'This alert could not be found.'
    default:           return fallback || 'Could not record the review.'
  }
}
